import { CONTENT_SERVICE_URL, LEGACY_URL } from 'config'
import { RequestBuilder, authorizedPipe } from 'services/net'

const MOCK_DELAY_MS = 2000

export interface AIConversationTextMessage {
  id: string
  role: 'user' | 'assistant'
  kind: 'text'
  text: string
}

export interface AIConversationImageMessage {
  id: string
  role: 'assistant'
  kind: 'image'
  imageUrl: string
  ratio: string
}

export type AIConversationMessage = AIConversationTextMessage | AIConversationImageMessage

export type AIConversationKind = 'text' | 'image'

export interface AIConversation {
  id: string
  title: string
  kind: AIConversationKind
  messages: AIConversationMessage[]
  createdAt: string
}

const MOCK_CONVERSATIONS: AIConversation[] = [
  {
    id: 'conv-1',
    kind: 'text',
    title: 'Product Launch Caption',
    createdAt: new Date(Date.now() - 86400000).toISOString(), // eslint-disable-line no-magic-numbers
    messages: [
      { id: 'u1', role: 'user', kind: 'text', text: 'Write a caption for my product launch photo' },
      {
        id: 'a1', role: 'assistant', kind: 'text',
        text: "Exciting news — our latest product is finally here! 🚀 We've poured our"
          + " hearts into this one and can't wait for you to experience it. Drop a comment below and let us know what you think!"
      }
    ]
  },
  {
    id: 'conv-2',
    kind: 'text',
    title: 'Monday Motivation Post',
    createdAt: new Date(Date.now() - 172800000).toISOString(), // eslint-disable-line no-magic-numbers
    messages: [
      { id: 'u2', role: 'user', kind: 'text', text: 'I need a motivational quote post for Monday morning' },
      {
        id: 'a2', role: 'assistant', kind: 'text',
        text: 'Monday mindset: every week is a fresh canvas — paint it boldly. 💪 '
          + 'What goal are you tackling first today? Share below!'
      },
      { id: 'u3', role: 'user', kind: 'text', text: 'Make it shorter' },
      { id: 'a3', role: 'assistant', kind: 'text', text: 'New week, new wins. What\'s your #MondayGoal? 💪' }
    ]
  },
  {
    id: 'conv-3',
    kind: 'image',
    title: 'Mountain Lake Image',
    createdAt: new Date(Date.now() - 259200000).toISOString(), // eslint-disable-line no-magic-numbers
    messages: [
      { id: 'u4', role: 'user', kind: 'text', text: 'A serene mountain lake at sunrise, photorealistic' },
      {
        id: 'a4', role: 'assistant', kind: 'image', ratio: '16:9',
        imageUrl: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200'
      },
      { id: 'u5', role: 'user', kind: 'text', text: 'Make it more vibrant with autumn colors' },
      {
        id: 'a5', role: 'assistant', kind: 'image', ratio: '16:9',
        imageUrl: 'https://images.unsplash.com/photo-1507041957456-9c397ce39c97?w=1200'
      }
    ]
  },
  {
    id: 'conv-4',
    kind: 'image',
    title: 'Cyberpunk City Image',
    createdAt: new Date(Date.now() - 345600000).toISOString(), // eslint-disable-line no-magic-numbers
    messages: [
      { id: 'u6', role: 'user', kind: 'text', text: 'A neon-lit cyberpunk city street at night' },
      {
        id: 'a6', role: 'assistant', kind: 'image', ratio: '9:16',
        imageUrl: 'https://images.unsplash.com/photo-1493612276216-ee3925520721?w=900'
      }
    ]
  }
]

export const V1 = Object.freeze({
  getTags: function () {
    const builder = new RequestBuilder(`${LEGACY_URL}/v3/AI_completions`).asPost().expectJSON()

    return function (text?: string, link?: string) {
      const data: FormData = new FormData()
      if (text) {
        data.append('post_status', text)
      }
      if (link) {
        data.append('post_link', link)
      }
      return authorizedPipe<any>(
        builder
          .form(data)
          .param('feature', 'tags')
          .build()
      )
    }
  }(),

  generateImage: function () {
    const builder = new RequestBuilder(`${LEGACY_URL}/v3/AI_completions`).asPost().expectJSON()

    return function (prompt: string, ratio: string, sessionId?: string) {
      const data: FormData = new FormData()
      if (prompt) {
        data.append('prompt_text', prompt)
      }
      if (ratio) {
        data.append('aspect_ratio', ratio)
      }
      if (sessionId) {
        data.append('session_id', sessionId)
      }
      return authorizedPipe<any>(
        builder
          .form(data)
          .param('feature', 'images')
          .build()
      )
    }
  }(),

  generateCaption: function () {
    const builder = new RequestBuilder(`${LEGACY_URL}/v3/AI_completions`).asPost().expectJSON()
    return function (prompt: string, conversationId?: string) {
      const data: FormData = new FormData()
      data.append('prompt_text', prompt)
      if (conversationId) {
        data.append('session_id', conversationId)
      }

      return authorizedPipe<any>(
        builder
          .body(data)
          .param('feature', 'text')
          .build()
      )
    }
  }(),

  getConversations: function () {
    // TODO: replace with authorizedPipe to real endpoint, e.g. GET /v3/AI_conversations
    return function () {
      // return () => of([...MOCK_CONVERSATIONS]).pipe(delay(500)) // eslint-disable-line no-magic-numbers
      const builder = new RequestBuilder(`${CONTENT_SERVICE_URL}/completions/get_user_chats`).asGet().expectJSON()
      return authorizedPipe<any>(
        builder.build()
      )
    }
  }(),

  deleteConversation: function () {
    return function (id: string) {
      const builder = new RequestBuilder(`${CONTENT_SERVICE_URL}/completions/hide_session/${id}`).asPost().expectJSON()
      return authorizedPipe<any>(
        builder.build()
      )
    }
  }()
})
